import { mdiCrosshairsGps, mdiDeleteOutline } from '@mdi/js';
import Icon from '@src/app-components/icon/icon';
import { TableCell } from '@src/app-components/table/table-cell-components/TableCell';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from '@trussworks/react-uswds';

const createDropdownOptions = (data) => {
  if (!data) return [];

  return data.map((item) => {
    const { code, description } = item;

    return {
      value: code,
      text: description,
    };
  });
};

const getRowIndex = (val) => val?.split('-')?.at('-1');

export const getTelemetryColumns = ({
  frequencyId,
  positionConfidence,
  spawnBehavior,
  mesos,
  macros,
  handleCaptureRow,
  canDeleteRow,
  onRemoveRow,
  online,
  isSearchTypeRs,
}) => {
  const columnHelper = createColumnHelper();

  return [
    columnHelper.accessor((row) => row?.tId ?? row?.t_id ?? row?.serverId ?? '', {
      id: 'tId',
      header: 'ID',
      cell: ({ cell }) => <span>{cell.getValue()}</span>,
      size: 30,
    }),
    columnHelper.accessor(
      (row) => {
        const tFid = row?.tFid ?? row?.t_fid;
        const tId = row?.tId ?? row?.t_id ?? row?.serverId;
        if (tFid) {
          return tFid;
        }
        if (!tId && row?.localDisplayId) {
          return row.localDisplayId;
        }
        return '';
      },
      {
        id: 'tFid',
        header: 'Field ID',
        cell: ({ cell }) => <span>{getRowIndex(cell.getValue())}</span>,
        size: 30,
      }
    ),
    columnHelper.accessor('bend', {
      header: 'Bend',
      cell: TableCell,
      size: 65,
      meta: { type: 'number', required: isSearchTypeRs },
    }),
    columnHelper.accessor('bendRiverMile', {
      header: 'River Mile',
      cell: TableCell,
      size: 70,
    }),
    columnHelper.accessor('radioTagNum', {
      header: 'Radio Tag #',
      cell: TableCell,
      size: 100,
      meta: { type: 'number', required: true },
    }),
    columnHelper.accessor('frequencyIdCode', {
      header: 'Frequency ID',
      cell: TableCell,
      meta: {
        type: 'select',
        required: true,
        options: createDropdownOptions(frequencyId),
      },
    }),
    columnHelper.accessor('captureButton', {
      header: 'Capture',
      cell: ({ row }) => (
        <Button className='primary-btn' onClick={() => handleCaptureRow(row.index)} type='button'>
          <Icon path={mdiCrosshairsGps} />
        </Button>
      ),
      size: 55,
    }),
    columnHelper.accessor('captureTime', {
      header: 'Capture Time',
      cell: TableCell,
      size: 100,
      meta: { type: 'text', required: true },
    }),
    columnHelper.accessor('captureLatitude', {
      header: 'Capture Latitude',
      cell: TableCell,
      size: 100,
      meta: { type: 'text', required: true },
    }),
    columnHelper.accessor('captureLongitude', {
      header: 'Capture Longitude',
      cell: TableCell,
      size: 100,
      meta: { type: 'text', required: true },
    }),
    columnHelper.accessor('positionConfidence', {
      header: 'Position Confidence',
      cell: TableCell,
      size: 100,
      meta: {
        type: 'select',
        required: true,
        options: createDropdownOptions(positionConfidence),
      },
    }),
    columnHelper.accessor('suspectedSpawningActivity', {
      header: 'Spawn Behavior',
      cell: TableCell,
      size: 150,
      meta: {
        type: 'select',
        options: createDropdownOptions(spawnBehavior),
      },
    }),
    columnHelper.accessor('macroId', {
      header: 'Macro',
      cell: TableCell,
      size: 150,
      meta: {
        type: 'select',
        options: createDropdownOptions(macros),
      },
    }),
    columnHelper.accessor('mesoId', {
      header: 'Meso',
      cell: TableCell,
      size: 150,
      meta: {
        type: 'select',
        options: createDropdownOptions(mesos),
      },
    }),
    columnHelper.accessor('depth', {
      header: 'Depth',
      cell: TableCell,
      size: 80,
      meta: { type: 'number' },
    }),
    columnHelper.accessor('temp', {
      header: 'Temp',
      cell: TableCell,
      size: 80,
      meta: { type: 'number' },
    }),
    columnHelper.accessor('conductivity', {
      header: 'Conductivity',
      cell: TableCell,
      size: 80,
      meta: { type: 'number' },
    }),
    columnHelper.accessor('turbidity', {
      header: 'Turbidity',
      cell: TableCell,
      size: 80,
      meta: { type: 'number' },
    }),
    columnHelper.accessor('silt', {
      header: 'Silt',
      cell: TableCell,
      size: 80,
      meta: { type: 'number' },
    }),
    columnHelper.accessor('sand', {
      header: 'Sand',
      cell: TableCell,
      size: 80,
      meta: { type: 'number' },
    }),
    columnHelper.accessor('gravel', {
      header: 'Gravel',
      cell: TableCell,
      size: 80,
      meta: { type: 'number' },
    }),
    columnHelper.accessor('comments', {
      header: 'Comments',
      cell: TableCell,
      size: 175,
      meta: { type: 'text', width: '9rem' },
    }),
    ...(online
      ? [
          columnHelper.accessor('editInitials', {
            header: 'Edit Initials',
            cell: TableCell,
            size: 75,
            meta: { type: 'text' },
          }),
          columnHelper.accessor('lastEditComment', {
            header: 'Last Edit Comment',
            cell: TableCell,
            size: 175,
            meta: { type: 'text' },
          }),
          columnHelper.accessor('checkby', {
            header: 'Check By',
            cell: TableCell,
            size: 75,
            meta: { type: 'text' },
          }),
          columnHelper.accessor('uploadedBy', {
            header: 'Uploaded By',
            cell: ({ cell }) => <span>{cell.getValue()}</span>,
            size: 100,
          }),
        ]
      : []),
    columnHelper.display({
      header: 'Delete',
      cell: ({ row }) =>
        canDeleteRow(row.original) ? (
          <Button
            aria-label='Delete unsaved telemetry row'
            className='secondary-btn'
            onClick={() => {
              if (window.confirm('Are you sure you want to delete this telemetry row?')) {
                onRemoveRow(row.index);
              }
            }}
            size='small'
            title='Delete unsaved telemetry row'
            type='button'
          >
            <Icon focusable={false} path={mdiDeleteOutline} size='16px' />
          </Button>
        ) : null,
      size: 50,
      enableSorting: false,
    }),
  ];
};
